import React, { useState, useEffect, useRef, useCallback } from "react";
import { Router, Route, Switch } from "react-router-dom";
import { Container } from "reactstrap";

import { RequestsProvider } from './context/RequestsContext';
import { TablesProvider } from "./context/TablesContext";
import { OrdersProvider } from "./context/OrdersContext";
import { VenueAlertSoundProvider } from "./context/VenueAlertSoundContext";
import { VenueFeedbackProvider } from "./context/VenueFeedbackContext";

import Loading from "./components/Loading";
import NavBar from "./components/NavBar";
import Profile from "./views/Profile";
import { useAuth0 } from "@auth0/auth0-react";
import history from "./utils/history";
import { useVenueId } from "./hooks/useUserData";

// fontawesome
import initFontAwesome from "./utils/initFontAwesome";
import OrdersPage from "./views/OrdersPage";
import TablesPage from "./views/TablesPage";
import RequestsPage from "./views/RequestsPage";
import CashierPage from "./views/CashierPage";
import ShiftPage from "./views/ShiftPage";
import "./styles/VenueShell.css";
import "./styles/VenueSurface.css";

initFontAwesome();

const NAVBAR_AUTO_HIDE_DELAY_MS = 800;
const NAVBAR_SCROLL_THRESHOLD = 24;
const NAVBAR_TOP_REVEAL_THRESHOLD = 12;
const NAVBAR_GESTURE_THRESHOLD = 18;
const NAVBAR_AUTO_HIDE_PATHS = new Set(['/orders', '/cashier']);

const shouldAutoHideNavbar = (pathname = '') => NAVBAR_AUTO_HIDE_PATHS.has(pathname);

const normalizeScrollTarget = (target) => {
  if (!target || target === window || target === document) {
    return window;
  }

  if (target === document.documentElement || target === document.body) {
    return window;
  }

  if (target instanceof Element) {
    return target;
  }

  return window;
};

const getScrollTop = (target) => {
  if (target === window) {
    return window.scrollY || document.documentElement.scrollTop || document.body.scrollTop || 0;
  }

  return target.scrollTop || 0;
};

const App = () => {
  const { isLoading, error } = useAuth0();
  const venueId = useVenueId();
  const [navbarVisible, setNavbarVisible] = useState(true);
  const lastScrollY = useRef(0);
  const activeScrollTarget = useRef(null);
  const autoHideTimer = useRef(null);
  const touchStartY = useRef(null);

  const clearAutoHideTimer = useCallback(() => {
    if (autoHideTimer.current !== null) {
      window.clearTimeout(autoHideTimer.current);
      autoHideTimer.current = null;
    }
  }, []);

  const scheduleAutoHide = useCallback(() => {
    clearAutoHideTimer();
    if (!shouldAutoHideNavbar(history.location.pathname)) {
      setNavbarVisible(true);
      return;
    }
    autoHideTimer.current = window.setTimeout(() => {
      setNavbarVisible(false);
    }, NAVBAR_AUTO_HIDE_DELAY_MS);
  }, [clearAutoHideTimer]);

  const resetNavbarForRoute = useCallback(() => {
    activeScrollTarget.current = null;
    lastScrollY.current = 0;
    touchStartY.current = null;
    setNavbarVisible(true);
    if (!shouldAutoHideNavbar(history.location.pathname)) {
      clearAutoHideTimer();
      return;
    }
    scheduleAutoHide();
  }, [clearAutoHideTimer, scheduleAutoHide]);

  const setNavbarFromGesture = useCallback((shouldShow) => {
    clearAutoHideTimer();
    setNavbarVisible(shouldShow);
  }, [clearAutoHideTimer]);

  const handleScrollIntent = useCallback((rawTarget) => {
    if (!shouldAutoHideNavbar(history.location.pathname)) {
      setNavbarVisible(true);
      return;
    }

    clearAutoHideTimer();

    const target = normalizeScrollTarget(rawTarget);
    const nextScrollTop = getScrollTop(target);

    if (nextScrollTop <= NAVBAR_TOP_REVEAL_THRESHOLD) {
      activeScrollTarget.current = target;
      lastScrollY.current = nextScrollTop;
      setNavbarVisible(true);
      return;
    }

    if (activeScrollTarget.current !== target) {
      activeScrollTarget.current = target;
      lastScrollY.current = nextScrollTop;
      return;
    }

    const previousScrollTop = lastScrollY.current;
    const delta = nextScrollTop - previousScrollTop;

    if (delta >= NAVBAR_SCROLL_THRESHOLD) {
      setNavbarVisible(false);
    } else if (delta <= -NAVBAR_SCROLL_THRESHOLD) {
      setNavbarVisible(true);
    }

    lastScrollY.current = nextScrollTop;
  }, [clearAutoHideTimer]);

  useEffect(() => {
    resetNavbarForRoute();

    const onWindowScroll = () => {
      handleScrollIntent(window);
    };

    const onDocumentScroll = (event) => {
      handleScrollIntent(event.target);
    };

    const onWheel = (event) => {
      if (!shouldAutoHideNavbar(history.location.pathname)) {
        setNavbarVisible(true);
        return;
      }

      if (Math.abs(event.deltaY) < NAVBAR_GESTURE_THRESHOLD) {
        return;
      }

      setNavbarFromGesture(event.deltaY < 0);
    };

    const onTouchStart = (event) => {
      if (!event.touches || event.touches.length === 0) {
        return;
      }

      touchStartY.current = event.touches[0].clientY;
    };

    const onTouchMove = (event) => {
      if (!shouldAutoHideNavbar(history.location.pathname)) {
        setNavbarVisible(true);
        touchStartY.current = null;
        return;
      }

      if (!event.touches || event.touches.length === 0 || touchStartY.current === null) {
        return;
      }

      const currentY = event.touches[0].clientY;
      const deltaY = currentY - touchStartY.current;

      if (Math.abs(deltaY) < NAVBAR_GESTURE_THRESHOLD) {
        return;
      }

      setNavbarFromGesture(deltaY > 0);
      touchStartY.current = currentY;
    };

    const onTouchEnd = () => {
      touchStartY.current = null;
    };

    window.addEventListener('scroll', onWindowScroll, { passive: true });
    window.addEventListener('wheel', onWheel, { passive: true });
    window.addEventListener('touchstart', onTouchStart, { passive: true });
    window.addEventListener('touchmove', onTouchMove, { passive: true });
    window.addEventListener('touchend', onTouchEnd, { passive: true });
    window.addEventListener('touchcancel', onTouchEnd, { passive: true });
    document.addEventListener('scroll', onDocumentScroll, { passive: true, capture: true });

    return () => {
      clearAutoHideTimer();
      window.removeEventListener('scroll', onWindowScroll);
      window.removeEventListener('wheel', onWheel);
      window.removeEventListener('touchstart', onTouchStart);
      window.removeEventListener('touchmove', onTouchMove);
      window.removeEventListener('touchend', onTouchEnd);
      window.removeEventListener('touchcancel', onTouchEnd);
      document.removeEventListener('scroll', onDocumentScroll, true);
    };
  }, [clearAutoHideTimer, handleScrollIntent, resetNavbarForRoute, setNavbarFromGesture]);

  useEffect(() => {
    const unlisten = history.listen(() => {
      resetNavbarForRoute();
    });

    return () => {
      unlisten();
      clearAutoHideTimer();
    };
  }, [clearAutoHideTimer, resetNavbarForRoute]);

  if (error) {
    return <div>Oops... {error.message}</div>;
  }

  if (isLoading) {
    return <Loading />;
  }

  return (
    <Router history={history}>
      <VenueFeedbackProvider>
        <div id="app" className={`venue-shell ${navbarVisible ? '' : 'venue-shell--nav-hidden'}`.trim()}>
          <OrdersProvider venueId={venueId}>
            <TablesProvider venueId={venueId}>
              <RequestsProvider venueId={venueId}>
                <VenueAlertSoundProvider>
                  <NavBar visible={navbarVisible} />
                  <main className="venue-shell-main">
                    <Container fluid className="venue-app-content">
                      <Switch>
                        <Route path="/orders" exact component={OrdersPage} />
                        <Route path="/tables" exact component={TablesPage} />
                        <Route path="/requests" exact component={RequestsPage} />
                        <Route path="/cashier" exact component={CashierPage} />
                        <Route path="/shift" exact component={ShiftPage} />
                        <Route path="/profile" component={Profile} />
                      </Switch>
                    </Container>
                  </main>
                </VenueAlertSoundProvider>
              </RequestsProvider>
            </TablesProvider>
          </OrdersProvider>
        </div>
      </VenueFeedbackProvider>
    </Router>
  );
};

export default App;
